(() => {
  // Only define if it doesn't already exist (prevents stock accordion conflicts)
  if (!customElements.get('accordion-tab')) {
    class AccordionTab extends HTMLElement {
      constructor() {
        super();

        this.details = this.querySelector('details');
        this.summary = this.querySelector('summary');
        this.content = this.querySelector('.accordion__content');

        this.animation = null;
        this.isClosing = false;
        this.isExpanding = false;

        if (typeof theme !== 'undefined' && theme.initWhenVisible) {
          theme.initWhenVisible({
            element: this,
            callback: this.init.bind(this),
            threshold: 200,
          });
        } else {
          this.init();
        }
      }

      init() {
        if (!this.summary) return;
        this.summary.addEventListener('click', (e) => this.onClick(e));
      }

      onClick(e) {
        e.preventDefault();

        this.details.style.overflow = 'hidden';
        if (this.isClosing || !this.details.open) {
          this.open();
        } else if (this.isExpanding || this.details.open) {
          this.shrink();
        }
      }

      shrink() {
        this.isClosing = true;

        const summaryStyle = window.getComputedStyle(this.summary);
        const startHeight = `${this.details.offsetHeight}px`;
        const endHeight = `${this.summary.offsetHeight + parseInt(summaryStyle.marginTop)}px`;

        if (this.animation) this.animation.cancel();

        this.animation = this.details.animate(
          { height: [startHeight, endHeight] },
          { duration: 250, easing: 'ease' }
        );

        this.toggleIcons(false);

        this.animation.onfinish = () => this.onAnimationFinish(false);
        this.animation.oncancel = () => (this.isClosing = false);
      }

      open() {
        this.details.style.height = `${this.details.offsetHeight}px`;
        this.details.open = true;

        this.toggleIcons(true);

        window.requestAnimationFrame(() => this.expand());
      }

      expand() {
        this.isExpanding = true;

        const summaryStyle = window.getComputedStyle(this.summary);
        const startHeight = `${this.details.offsetHeight}px`;
        const endHeight = `${
          this.summary.offsetHeight +
          parseInt(summaryStyle.marginTop) +
          (this.content?.offsetHeight || 0)
        }px`;

        if (this.animation) this.animation.cancel();

        this.animation = this.details.animate(
          { height: [startHeight, endHeight] },
          { duration: 400, easing: 'ease-out' }
        );

        this.animation.onfinish = () => this.onAnimationFinish(true);
        this.animation.oncancel = () => (this.isExpanding = false);
      }

      onAnimationFinish(open) {
        this.details.open = open;
        this.animation = null;
        this.isClosing = false;
        this.isExpanding = false;
        this.details.style.height = '';
        this.details.style.overflow = '';
      }

      toggleIcons(isOpen) {
        const iconPlus = this.summary?.querySelector('.icon-plus');
        const iconMinus = this.summary?.querySelector('.icon-minus');
        if (iconPlus && iconMinus) {
          iconPlus.style.display = isOpen ? 'none' : 'inline-block';
          iconMinus.style.display = isOpen ? 'inline-block' : 'none';
        }
      }
    }

    customElements.define('accordion-tab', AccordionTab);
  }

  // CollageList: also guard this define
  if (!customElements.get('collage-list')) {
    class CollageList extends HTMLElement {
      constructor() {
        super();

        this.isAccordion = this.hasAttribute('data-accordion');
        this.headers = Array.from(this.querySelectorAll('.collage-tabs-header'));

        this.onClick = this.onClick.bind(this);
        this.onMouseOver = this.onMouseOver.bind(this);
        this.onDocClick = this.onDocClick.bind(this);
        this.onResize = this.onResize.bind(this);

        this.addEventListener('click', this.onClick);

        if (!this.isAccordion) this.addEventListener('mouseover', this.onMouseOver);

        if (this.isAccordion) {
          this.initAccordionPanels();
          window.addEventListener('resize', this.onResize);
        }

        document.addEventListener('click', this.onDocClick);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('mouseover', this.onMouseOver);
        document.removeEventListener('click', this.onDocClick);
        window.removeEventListener('resize', this.onResize);
      }

      onClick(e) {
        const dot = e.target.closest('[data-lookbook-dot]');
        if (dot && this.contains(dot)) {
          e.preventDefault();
          e.stopPropagation();
          this.toggleHotspot(dot);
          return;
        }

        const header = e.target.closest('.collage-tabs-header');
        if (!header || !this.contains(header)) return;

        const blockId = header.dataset.blockId;
        if (!blockId) return;

        this.closeAllHotspots();

        if (this.isAccordion) {
          const isOpen = header.classList.contains('is-active');
          this.pauseAllMedia();
          if (isOpen) {
            this.setActive(null);
            return;
          }
          this.setActive(blockId);
          return;
        }

        if (header.classList.contains('is-active')) return;

        this.pauseAllMedia();
        this.setActive(blockId);
      }

      onMouseOver(e) {
        if (this.isAccordion) return;

        const header = e.target.closest('.collage-tabs-header');
        if (!header || !this.contains(header)) return;

        const blockId = header.dataset.blockId;
        if (!blockId) return;

        if (header.classList.contains('is-active')) return;

        this.pauseAllMedia();
        this.closeAllHotspots();
        this.setActive(blockId);
      }

      onDocClick(e) {
        if (!this.contains(e.target)) this.closeAllHotspots();
      }

      onResize() {
        if (!this.isAccordion) return;
        this.refreshOpenPanels();
      }

      setActive(blockId) {
        this.querySelectorAll('[data-block-id]').forEach((el) => {
          const match = blockId && el.dataset.blockId === blockId;
          el.classList.toggle('is-active', !!match);
        });

        if (this.isAccordion) {
          this.headers.forEach((h) => {
            const panel = this.getPanelForHeader(h);
            if (!panel) return;

            if (blockId && h.dataset.blockId === blockId) {
              h.classList.add('is-active');
              this.setPanelOpen(panel);
            } else {
              h.classList.remove('is-active');
              this.setPanelClosed(panel);
            }
          });
        }
      }

  initAccordionPanels() {
  this.headers.forEach((header) => {
    const panel = this.getPanelForHeader(header);
    if (!panel) return;

    panel.style.display = 'block';
    panel.style.overflow = 'hidden';
    panel.style.transition =
      'max-height 520ms cubic-bezier(0.22, 1, 0.36, 1), opacity 320ms ease, transform 520ms cubic-bezier(0.22, 1, 0.36, 1)';
    panel.style.willChange = 'max-height, opacity, transform';

    if (!header.classList.contains('is-active')) {
      panel.style.maxHeight = '0px';
      panel.style.opacity = '0';
      panel.style.transform = 'translateY(-6px)';
    } else {
      panel.style.opacity = '1';
      panel.style.transform = 'translateY(0)';
      panel.style.maxHeight = panel.scrollHeight + 'px';
    }
  });
}

refreshOpenPanels() {
  this.headers.forEach((header) => {
    if (!header.classList.contains('is-active')) return;
    const panel = this.getPanelForHeader(header);
    if (!panel) return;
    panel.style.maxHeight = panel.scrollHeight + 'px';
  });
}

getPanelForHeader(header) {
  const next = header.nextElementSibling;
  if (next && next.classList.contains('collage-description')) return next;
  return null;
}

setPanelOpen(panel) {
  panel.style.display = 'block';
  panel.style.opacity = '1';
  panel.style.transform = 'translateY(0)';

  requestAnimationFrame(() => {
    panel.style.maxHeight = panel.scrollHeight + 'px';
  });
}

setPanelClosed(panel) {
  panel.style.maxHeight = panel.scrollHeight + 'px';

  requestAnimationFrame(() => {
    panel.style.maxHeight = '0px';
    panel.style.opacity = '0';
    panel.style.transform = 'translateY(-6px)';
  });
}

      toggleHotspot(dot) {
        const hotspot = dot.closest('.lookbook-hotspot');
        const pop = hotspot?.querySelector('.lookbook-pop');
        if (!hotspot || !pop) return;

        const isOpen = hotspot.classList.contains('is-open');
        this.closeAllHotspots();

        if (!isOpen) {
          hotspot.classList.add('is-open');
          dot.setAttribute('aria-expanded', 'true');
        } else {
          hotspot.classList.remove('is-open');
          dot.setAttribute('aria-expanded', 'false');
        }
      }

      closeAllHotspots() {
        this.querySelectorAll('.lookbook-hotspot.is-open').forEach((hs) => {
          hs.classList.remove('is-open');
          const dot = hs.querySelector('[data-lookbook-dot]');
          if (dot) dot.setAttribute('aria-expanded', 'false');
        });
      }

      pauseAllMedia() {
        this.querySelectorAll('video-section[data-type="youtube"] iframe').forEach((video) => {
          video.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        });

        this.querySelectorAll('video-section[data-type="vimeo"] iframe').forEach((video) => {
          video.contentWindow?.postMessage('{"method":"pause"}', '*');
        });

        this.querySelectorAll('video').forEach((video) => video.pause());
        document.querySelectorAll('video').forEach((video) => video.pause());
      }
    }

    customElements.define('collage-list', CollageList);
  }
})();
